import { Box, Checkbox, FormControl, InputLabel, ListItemText, MenuItem, Select, Typography } from '@mui/material';

import RadiusButton from '../../../../components/RadiusButton';

export const ALL_VENUES = 'all';
// value of the Clear item; ticking it empties the selection (all)
const CLEAR_OPTION = '__clear__';

const VENUE_FIELD = 'venue';

// The first row, in this order; the venue sits between the exam filters and the lab.
const examFields = [
    { id: 'driveId', label: 'Drive', optionsKey: 'drives' },
    { id: 'examDate', label: 'Exam Date', optionsKey: 'examDates' },
    { id: 'courseId', label: 'Course', optionsKey: 'courses' },
    { id: 'batchId', label: 'Batch', optionsKey: 'batches' },
    { id: VENUE_FIELD, label: 'Venue' },
    { id: 'labNo', label: 'Lab', optionsKey: 'labs' },
];

export const EMPTY_AUDIT_OPTIONS = { venues: [], drives: [], courses: [], examDates: [], batches: [], labs: [], facets: [], summary: [] };

// drives / courses / batches come as { id, name }; exam dates and labs as plain values
const toOption = (option) => (typeof option === 'object' && option !== null
    ? { value: String(option.id), label: option.name }
    : { value: String(option), label: String(option) });

// The exam filters with only the values the venues' options still offer; a filter left with none
// is dropped.
export const offeredExamFilters = (examFilters, options) => {
    const kept = {};
    for (const field of examFields.filter((item) => item.optionsKey && examFilters[item.id])) {
        const offered = (options[field.optionsKey] ?? []).map((option) => toOption(option).value);
        const values = examFilters[field.id].filter((value) => offered.includes(value));
        if (values.length) kept[field.id] = values;
    }
    return kept;
};

// A dropdown's options: values found in the data that it does not always offer (e.g. RAM sizes),
// in natural order, then the ones it always offers.
const facetOptions = (facet, facetCounts, ticked = []) => {
    const found = Object.keys(facetCounts?.values ?? {}).filter((value) => !facet.options.includes(value))
        .sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
    const all = [...found, ...facet.options];
    // Only values some device has, and what is ticked, so it can be unticked.
    return facetCounts ? all.filter((value) => (facetCounts.values?.[value] ?? 0) > 0 || ticked.includes(value)) : all;
};

// A ticked option keeps a light background, so its checkbox shows (the app-wide .Mui-selected
// fills it with the primary colour).
const optionSx = {
    '&.Mui-selected, &.Mui-selected:hover, &.Mui-selected.Mui-focusVisible': { backgroundColor: '#EEF0FF !important', color: 'inherit !important' },
};

// A dropdown where any number of options can be ticked; none ticked means all, and Clear unticks
// them all. options are { value, label, count }.
const MultiFilterSelect = ({ id, label, values = [], options, allLabel = 'All', onChange, width = 200 }) => {
    const labelOf = (value) => options.find((option) => option.value === value)?.label ?? value;
    return (
        <FormControl className='form-field' sx={{ width }}>
            <InputLabel id={`${id}Label`} shrink>{label}</InputLabel>
            <Select labelId={`${id}Label`} id={id} label={label} size='small' multiple displayEmpty notched value={values}
                onChange={(event) => onChange(event.target.value.includes(CLEAR_OPTION) ? [] : event.target.value)}
                renderValue={(selected) => (!selected.length ? allLabel : selected.length === 1 ? labelOf(selected[0]) : `${selected.length} selected`)}
                MenuProps={{ PaperProps: { sx: { maxHeight: 420 } } }}>
                <MenuItem value={CLEAR_OPTION} dense disabled={!values.length} sx={{ color: 'primary.main', fontWeight: 600, borderBottom: '1px solid', borderColor: 'divider' }}>
                    Clear
                </MenuItem>
                {options.map((option) => (
                    <MenuItem key={option.value} value={option.value} dense sx={optionSx}>
                        <Checkbox size='small' checked={values.includes(option.value)} sx={{ p: 0.5, mr: 1 }} />
                        <ListItemText primary={option.label} />
                        {option.count !== undefined && <Typography variant='caption' sx={{ color: 'text.secondary', ml: 2 }}>{option.count}</Typography>}
                    </MenuItem>
                ))}
            </Select>
        </FormControl>
    );
};

// Venues, exam filters (which devices are counted) and one dropdown per hardware property; each
// can take several values. Each option shows how many devices it would add given every other
// filter.
export const HardwareAuditFilters = ({
    venueIds, options, examFilters, facetFilters, counts, matchedCount, scopeCount,
    onVenueChange, onExamChange, onFacetChange, onReset, onDownload, isDownloading,
}) => (
    <Box sx={{ mb: 2 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2, mb: 2 }}>
            {examFields.map((field) => (field.id === VENUE_FIELD
                ? <MultiFilterSelect key={field.id} id={field.id} label={field.label} values={venueIds} allLabel='All venues' width={240}
                    options={options.venues.map((venue) => ({ value: String(venue.id), label: venue.venueName }))}
                    onChange={onVenueChange} />
                : <MultiFilterSelect key={field.id} id={field.id} label={field.label} values={examFilters[field.id]}
                    options={(options[field.optionsKey] ?? []).map(toOption)}
                    onChange={(values) => onExamChange(field.id, values)} />
            ))}
        </Box>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2, mb: 2 }}>
            {options.facets.map((facet) => {
                const facetCounts = counts?.[facet.key];
                return (
                    <MultiFilterSelect key={facet.key} id={facet.key} label={facet.name} values={facetFilters[facet.key]}
                        allLabel={`All${facetCounts ? ` (${facetCounts.ALL})` : ''}`}
                        options={facetOptions(facet, facetCounts, facetFilters[facet.key]).map((value) => ({ value, label: value, count: facetCounts ? facetCounts.values?.[value] ?? 0 : undefined }))}
                        onChange={(values) => onFacetChange(facet.key, values)} />
                );
            })}
        </Box>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2 }}>
            <Typography sx={{ fontWeight: 500 }}>Filtered Count: {matchedCount}</Typography>
            <Typography sx={{ fontWeight: 500 }}>Total Count: {scopeCount}</Typography>
            <Box sx={{ ml: 'auto', display: 'flex', gap: 2 }}>
                <RadiusButton variant='outlined' color='secondary' onClick={onReset}>
                    Reset Filters
                </RadiusButton>
                <RadiusButton variant='contained' color='secondary' onClick={onDownload} disabled={isDownloading || !matchedCount}>
                    {isDownloading ? 'Downloading...' : 'Download CSV'}
                </RadiusButton>
            </Box>
        </Box>
    </Box>
);
